import { useCallback, useEffect, useRef, useState } from 'react'
import { post } from '../lib/api'
import { ferry, type TodaySailing } from '../lib/ferry'
import { t } from '../lib/i18n'

/**
 * The crew reporter.
 *
 * ── What this screen is, and is not ──────────────────────────────────────────
 *
 * It is not a map — `RouteMap` already draws the boat, and nothing here needs
 * to duplicate it. It is one loop: get a fix, send it, wait, repeat — built to
 * survive the two things a real crossing does to it: the signal drops for a
 * while, and the phone should not be dead by the return trip.
 *
 * ── Cadence: once a minute ───────────────────────────────────────────────────
 *
 * `RouteMap` polls the read side on a 60s timer, so reporting faster than that
 * buys nobody anything they could ever see. A crossing is 35–65 minutes, so
 * that is 35–65 fixes — enough for the dot to move honestly. One
 * `getCurrentPosition` per minute also lets the GPS radio idle between reads;
 * `watchPosition` was rejected because it keeps the radio hot continuously for
 * the whole crossing to serve updates nobody consumes that often — the
 * difference is exactly what flattens a phone over a working day of crossings.
 *
 * ── A fix that fails to send ─────────────────────────────────────────────────
 *
 * It is dropped. There is deliberately no offline queue: a queue would have to
 * replay a stale fix behind whatever the NEXT tick already sent, in order,
 * after the network came back — stateful machinery a once-a-minute loop does
 * not need to be correct. The loop already tries again in under sixty
 * seconds, which on a moving boat is a better answer than a replayed queue
 * entry would be anyway.
 */

const REPORT_EVERY_MS = 60_000

interface ReportResponse { ok: true; progress: number | null; speedKnots: number | null }

type LastAttempt =
  | { outcome: 'sent'; at: number; progress: number | null; speedKnots: number | null }
  | { outcome: 'rejected'; at: number }
  | { outcome: 'failed'; at: number }
  | null

function codeOf(e: unknown): string | number | undefined {
  return typeof e === 'object' && e !== null && 'code' in e
    ? (e as { code?: string | number }).code
    : undefined
}

/** One fix, not a continuous watch — see the module note above for why. */
function getFix(): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    if (!('geolocation' in navigator)) { reject(new Error('unsupported')); return }
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      timeout: 20_000,
      // Reuse a fix the OS already has if it is under 20s old, instead of
      // forcing a fresh radio read on every single tick.
      maximumAge: 20_000,
    })
  })
}

export default function CrewReporter() {
  const [sailings, setSailings] = useState<TodaySailing[] | null>(null)
  const [sailingId, setSailingId] = useState<string | null>(null)
  const [running, setRunning] = useState(false)
  const [autoStopped, setAutoStopped] = useState(false)
  const [locationError, setLocationError] = useState('')
  const [last, setLast] = useState<LastAttempt>(null)
  const [counts, setCounts] = useState({ sent: 0, failed: 0, rejected: 0 })

  const timer = useRef<number | null>(null)
  // The interval callback closes over the first render; the ref is what lets
  // it always see the CURRENT selection without re-creating the interval.
  const sailingIdRef = useRef<string | null>(null)
  useEffect(() => { sailingIdRef.current = sailingId }, [sailingId])

  // Same 60s poll `Ops`' Today board uses — which sailing is underway changes
  // while this screen is open, and a boat that just arrived should not still
  // look reportable.
  useEffect(() => {
    let live = true
    const load = () => {
      void ferry.ops.today().then((d) => { if (live) setSailings(d.sailings) }).catch(() => { if (live) setSailings([]) })
    }
    load()
    const poll = window.setInterval(load, 60_000)
    return () => { live = false; window.clearInterval(poll) }
  }, [])

  const underway = (sailings ?? []).filter((s) => s.status === 'departed')

  const stop = useCallback(() => {
    if (timer.current != null) window.clearInterval(timer.current)
    timer.current = null
    setRunning(false)
  }, [])

  const stopBecauseArrived = useCallback(() => {
    stop()
    setAutoStopped(true)
  }, [stop])

  /*
   * If the sailing this screen is reporting for stops being underway, there is
   * nothing left to report. The SERVER would refuse the next tick anyway
   * (`not_underway` — see the matching branch in `tick`), so this is a battery
   * optimisation, not the enforcement point: it just saves finding that out
   * again every sixty seconds.
   */
  useEffect(() => {
    if (running && sailingId && !underway.some((s) => s.id === sailingId)) stopBecauseArrived()
  }, [running, sailingId, underway, stopBecauseArrived])

  const tick = useCallback(async () => {
    const id = sailingIdRef.current
    if (!id) return
    try {
      const fix = await getFix()
      const body = {
        lat: fix.coords.latitude,
        lng: fix.coords.longitude,
        heading: fix.coords.heading ?? null,
        // The GPS clock, not send time — the server keys the speed check off
        // this, so a slow request over patchy coverage must not read as
        // though the boat covered its distance in less time than it really
        // took.
        at: fix.timestamp,
      }
      const res = await post<ReportResponse>(`/api/ferry/sailings/${id}/position`, body)
      setLast({ outcome: 'sent', at: Date.now(), progress: res.progress, speedKnots: res.speedKnots })
      setCounts((c) => ({ ...c, sent: c.sent + 1 }))
      setLocationError('')
    } catch (e) {
      const code = codeOf(e)
      if (code === 'not_underway') {
        stopBecauseArrived()
      } else if (code === 'impossible_movement') {
        setLast({ outcome: 'rejected', at: Date.now() })
        setCounts((c) => ({ ...c, rejected: c.rejected + 1 }))
      } else if (e instanceof Error && e.message === 'unsupported') {
        setLocationError(t('crew.locationUnsupported'))
      } else if (code === GeolocationPositionError.PERMISSION_DENIED) {
        setLocationError(t('crew.locationDenied'))
      } else {
        /*
         * Everything else lands here: a GPS timeout, no signal, a failed
         * request. THE FIX IS LOST — see the module note on why there is no
         * queue — and the loop simply continues to the next tick regardless.
         * A failed send must never stop the interval; the whole point of the
         * cadence is that the next attempt is under a minute away.
         */
        setLast({ outcome: 'failed', at: Date.now() })
        setCounts((c) => ({ ...c, failed: c.failed + 1 }))
      }
    }
  }, [stopBecauseArrived])

  function start() {
    if (!sailingId) return
    setLocationError('')
    setAutoStopped(false)
    setCounts({ sent: 0, failed: 0, rejected: 0 })
    setLast(null)
    setRunning(true)
    void tick()
    timer.current = window.setInterval(() => void tick(), REPORT_EVERY_MS)
  }

  // Leaving the screen must not leave the loop running in the background.
  useEffect(() => stop, [stop])

  if (sailings === null) return <section className="card"><p className="muted">{t('app.loading')}</p></section>

  return (
    <section className="card crew-reporter">
      <h1>{t('crew.title')}</h1>
      <p className="muted">{t('crew.intro')}</p>

      {autoStopped && !running && <p className="muted">{t('crew.stoppedArrived')}</p>}

      {!running && (
        underway.length === 0 ? (
          <p className="muted">{t('crew.noneUnderway')}</p>
        ) : (
          <>
            <p><strong>{t('crew.pickSailing')}</strong></p>
            <div className="crew-picker">
              {underway.map((s) => (
                <button key={s.id} aria-current={sailingId === s.id} onClick={() => setSailingId(s.id)}>
                  {s.route.split('-').join(' → ')} · {s.vessel_name}
                </button>
              ))}
            </div>
            <button className="primary big" disabled={!sailingId} onClick={start}>
              {t('crew.start')}
            </button>
          </>
        )
      )}

      {running && (
        <>
          <p>{t('crew.running')}</p>
          <button onClick={stop}>{t('crew.stop')}</button>

          <p className="muted small crew-tally">
            {counts.sent} {t('crew.sent')} · {counts.failed} {t('crew.failed')} · {counts.rejected} {t('crew.rejected')}
          </p>

          {last?.outcome === 'sent' && (
            <p className="muted small">
              {t('crew.lastAt')} {new Date(last.at).toLocaleTimeString()}
              {last.progress != null ? ` · ${Math.round(last.progress * 100)}%` : ''}
              {last.speedKnots != null ? ` · ${Math.round(last.speedKnots)} kn` : ''}
            </p>
          )}
        </>
      )}

      {locationError && <p className="error">{locationError}</p>}
    </section>
  )
}
